import { Injectable, inject } from '@angular/core';
import { GsapService } from '@app/shared/animations/gsap.service';

@Injectable()
export class UnlockCasinoAnimations {
  private readonly DISTANCES = {
    JULIET_SLIDE: 300,
    JULIET_EXIT: 300,
    CONFETTI_SWAY_BACK: 15,
    CONFETTI_SWAY_FRONT: 25,
    CASINO_CENTER_OFFSET: 200,
  };

  private readonly DURATIONS = {
    JULIET_SLIDE: 0.8,
    JULIET_EXIT: 0.6,
    CONFETTI_POP_BACK: 0.2,
    CONFETTI_POP_FRONT: 0.4,
    CONFETTI_SWING_BACK: 3,
    CONFETTI_SWING_FRONT: 2.5,
    CASINO_METEOR: 0.7,
    CASINO_SLIDE_UP: 0.8,
    REWARD_SEQUENCE: 0.5,
    REWARD_STAGGER: 0.5,
    SLOTS_SEQUENCE: 0.4,
    SLOTS_STAGGER: 0.1,
  };

  private readonly gsapService = inject(GsapService);

  julietEntrance(element: HTMLElement, onComplete?: () => void): void {
    this.gsapService.slideIn(element, 'bottom', this.DISTANCES.JULIET_SLIDE, {
      duration: this.DURATIONS.JULIET_SLIDE,
      onComplete,
    });
  }

  /**
   * Animates Juliet character exiting the scene
   */
  julietExit(element: HTMLElement): void {
    this.gsapService.slideOut(element, 'left', this.DISTANCES.JULIET_EXIT, {
      duration: this.DURATIONS.JULIET_EXIT,
    });
  }

  confettiCelebration(backElement: HTMLElement, frontElement: HTMLElement, stop: boolean): void {
    this.gsapService.popIn(backElement, {
      delay: this.DURATIONS.CONFETTI_POP_BACK,
      onComplete: () => {
        if (!stop) {
          this.gsapService.swing(backElement, {
            rotation: 0,
            x: this.DISTANCES.CONFETTI_SWAY_BACK,
            duration: this.DURATIONS.CONFETTI_SWING_BACK,
          });
        }
      },
    });
    this.gsapService.popIn(frontElement, {
      delay: this.DURATIONS.CONFETTI_POP_FRONT,
      onComplete: () => {
        if (!stop) {
          this.gsapService.swing(frontElement, {
            rotation: 0,
            x: this.DISTANCES.CONFETTI_SWAY_FRONT,
            duration: this.DURATIONS.CONFETTI_SWING_FRONT,
          });
        }
      },
    });
  }

  casinoLogoReveal(element: HTMLElement, onComplete?: () => void): void {
    this.gsapService.set(element, {
      scale: 5,
      opacity: 0,
      z: 100,
      y: this.DISTANCES.CASINO_CENTER_OFFSET,
    });
    this.gsapService.to(
      element,
      {
        scale: 1,
        opacity: 1,
        z: 0,
        y: this.DISTANCES.CASINO_CENTER_OFFSET,
      },
      {
        duration: this.DURATIONS.CASINO_METEOR,
        ease: 'expo.out',
        onComplete,
      }
    );
  }

  casinoLogoToTop(element: HTMLElement, onComplete?: () => void): void {
    this.gsapService.to(
      element,
      { y: 0 },
      {
        duration: this.DURATIONS.CASINO_SLIDE_UP,
        ease: 'power2.inOut',
        onComplete,
      }
    );
  }

  casinoInfoReveal(element: HTMLElement, onComplete?: () => void): void {
    this.gsapService.set(element, { opacity: 1 });
    this.gsapService.popIn(element, { onComplete });
  }

  rewardsSequence(rewardElements: HTMLElement[], buttonElement: HTMLElement, onComplete?: () => void): void {
    const allElements = [...rewardElements, buttonElement];

    const timeline = this.gsapService.createTimeline({ onComplete });

    timeline.set(allElements, { scale: 0, opacity: 0 });

    timeline.to(rewardElements, {
      scale: 1,
      opacity: 1,
      duration: this.DURATIONS.REWARD_SEQUENCE,
      ease: 'back.out(1.7)',
      stagger: this.DURATIONS.REWARD_STAGGER,
    });

    timeline.to(buttonElement, {
      scale: 1,
      opacity: 1,
      duration: this.DURATIONS.REWARD_SEQUENCE,
      ease: 'elastic.out(1, 0.5)',
    });
  }

  slotsTitleReveal(titleElement?: HTMLElement, displayElement?: HTMLElement): void {
    if (titleElement) {
      this.gsapService.zoomIn(titleElement, { delay: 0 });
    }
    if (displayElement) {
      this.gsapService.zoomIn(displayElement, { delay: 0.2 });
    }
  }

  slotsSequence(slotElements: HTMLElement[], onComplete?: () => void): void {
    this.gsapService.sequence(slotElements, {
      duration: this.DURATIONS.SLOTS_SEQUENCE,
      stagger: this.DURATIONS.SLOTS_STAGGER,
      from: { scale: 0, opacity: 0 },
      to: { scale: 1, opacity: 1 },
      onComplete,
    });
  }

  exitElements(elements: HTMLElement[]): void {
    elements.forEach((element, index) => {
      this.gsapService.zoomOut(element, { delay: index * 0.1 });
    });
  }
}
